import React, { useRef, useState, useEffect } from 'react';
import {
  CanvasContainer,
  Canvas,
  CanvasWrapper,
  ButtonGroup,
  ClearButton,
  Instructions,
  SignatureLabel,
} from './SignatureCanvas.styles';

/**
 * SignatureCanvas Component
 *
 * Mobile-first signature canvas with touch and mouse support
 * Story 1: Digital Document Generation & Signature System
 */
const SignatureCanvas = ({ onSignatureChange, width = 400, height = 200 }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    setContext(ctx);

    // Set canvas size based on container
    const container = canvas.parentElement;
    const rect = container.getBoundingClientRect();

    canvas.width = rect.width;
    canvas.height = height;

    // Set drawing styles
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Handle window resize
    const handleResize = () => {
      const newRect = container.getBoundingClientRect();
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      canvas.width = newRect.width;
      canvas.height = height;
      ctx.putImageData(imageData, 0, 0);

      // Restore drawing styles after resize
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [height]);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Handle touch events
    if (event.touches && event.touches.length > 0) {
      return {
        x: event.touches[0].clientX - rect.left,
        y: event.touches[0].clientY - rect.top,
      };
    }

    // Handle mouse events
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startDrawing = (event) => {
    event.preventDefault();
    if (!context) return;

    setIsDrawing(true);
    const { x, y } = getCoordinates(event);

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (event) => {
    event.preventDefault();
    if (!isDrawing || !context) return;

    const { x, y } = getCoordinates(event);
    context.lineTo(x, y);
    context.stroke();

    setHasSignature(true);
  };

  const stopDrawing = (event) => {
    event.preventDefault();
    if (!isDrawing) return;

    setIsDrawing(false);

    // Notify parent component of signature change
    if (hasSignature && onSignatureChange) {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      onSignatureChange(signatureData);
    }
  };

  const clearSignature = () => {
    if (!context || !canvasRef.current) return;

    const canvas = canvasRef.current;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    setHasSignature(false);

    if (onSignatureChange) {
      onSignatureChange(null);
    }
  };

  const getSignatureData = () => {
    if (!hasSignature || !canvasRef.current) return null;
    return canvasRef.current.toDataURL('image/png');
  };

  // Expose getSignatureData method via ref (optional)
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.getSignatureData = getSignatureData;
      canvasRef.current.clearSignature = clearSignature;
    }
  }, [hasSignature]);

  return (
    <CanvasContainer>
      <SignatureLabel>Tanda Tangan Anda</SignatureLabel>
      <Instructions>
        Gunakan jari atau mouse untuk menggambar tanda tangan Anda di area bawah
      </Instructions>

      <CanvasWrapper>
        <Canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
      </CanvasWrapper>

      <ButtonGroup>
        <ClearButton
          type="button"
          onClick={clearSignature}
          disabled={!hasSignature}
        >
          🗑️ Hapus & Ulangi
        </ClearButton>
      </ButtonGroup>
    </CanvasContainer>
  );
};

export default SignatureCanvas;
